// Hash router: #<view>/<id>/<id>… Unknown → not-found. A pending edit keeps the current route mounted.
import { useSyncExternalStore } from "react";

/**
 * The screen an empty hash opens: the first destination after sign-in, and after a sign-out reload to "/". Every
 * persona lands on Home (plan D17, reader 7 D1); V1's Today stays hosted at #today.
 */
export const LANDING_VIEW = "home";

export interface Route {
  view: string;
  parts: string[];
  raw: string;
  /** The query after "?" in the hash (`#action-center?kind=approvals&sort=due`): view state only, never identity. */
  params: URLSearchParams;
}

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#/, "");
  const at = raw.indexOf("?");
  const path = at < 0 ? raw : raw.slice(0, at);
  const [view = LANDING_VIEW, ...parts] = path.split("/").filter(Boolean);
  return { view, parts, raw, params: new URLSearchParams(at < 0 ? "" : raw.slice(at + 1)) };
}

type Blocker = (proceed: () => void) => void;
const blockers = new Set<Blocker>();
const listeners = new Set<() => void>();
// The browser's entry index also covers a same-URL navigation or reload that replaced history.state.
// Older engines use the index stamped on every entry this router sees.
const historyPosition = () => (window as unknown as { navigation?: { currentEntry?: { index: number } } }).navigation?.currentEntry?.index;
let current = parseHash(window.location.hash);
let currentIndex = historyPosition() ?? Number(window.history.state?.taRouteIndex ?? 0);
let acceptedHash: string | null = null;
let restoring: (() => void) | null = null;
window.history.replaceState({ ...window.history.state, taRouteIndex: currentIndex }, "");

export function blockNavigation(blocker: Blocker): () => void {
  blockers.add(blocker);
  return () => { blockers.delete(blocker); };
}

function blocked(proceed: () => void): boolean {
  const blocker = [...blockers].at(-1);
  if (!blocker) return false;
  blocker(proceed);
  return true;
}

function commit(index: number): void {
  currentIndex = index;
  current = parseHash(window.location.hash);
  for (const notify of listeners) notify();
}

function onHistoryChange(): void {
  const nextHash = window.location.hash;
  const stampedIndex = Number(window.history.state?.taRouteIndex ?? currentIndex + 1);
  const nextIndex = historyPosition() ?? (stampedIndex === currentIndex && nextHash !== href(current.raw) ? currentIndex + 1 : stampedIndex);
  if (restoring) {
    // A traversal emits popstate and hashchange. Ignore the departing entry's second event while
    // compensation is in flight; ask only after the browser has returned to the retained editor.
    if (nextHash === href(current.raw) && nextIndex === currentIndex) {
      const restored = restoring;
      restoring = null;
      restored();
    }
    return;
  }
  if (nextHash === href(current.raw) && nextIndex === currentIndex) return;
  window.history.replaceState({ ...window.history.state, taRouteIndex: nextIndex }, "");
  if (acceptedHash === nextHash) {
    acceptedHash = null;
    commit(nextIndex);
    return;
  }
  const distance = nextIndex - currentIndex;
  if (blockers.size && distance) {
    restoring = () => blocked(() => {
      acceptedHash = nextHash;
      window.history.go(distance);
    });
    window.history.go(-distance);
    return;
  }
  commit(nextIndex);
}
window.addEventListener("popstate", onHistoryChange);
window.addEventListener("hashchange", onHistoryChange);

function visit(destination: string): void {
  const oldURL = window.location.href;
  // Stamp the leaving entry too: a same-URL browser navigation may have cleared its state.
  window.history.replaceState({ ...window.history.state, taRouteIndex: currentIndex }, "");
  window.history.pushState({ taRouteIndex: currentIndex + 1 }, "", destination);
  commit(historyPosition() ?? currentIndex + 1);
  window.dispatchEvent(new HashChangeEvent("hashchange", { oldURL, newURL: window.location.href }));
}

// Ordinary links are intercepted before the URL changes. Ctrl/Cmd-click and external links keep
// browser behavior; full-document departures are covered by the form's beforeunload handler.
document.addEventListener("click", (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const anchor = (event.target instanceof Element ? event.target : event.target instanceof Node ? event.target.parentElement : null)?.closest<HTMLAnchorElement>("a[href]");
  if (!anchor || anchor.hasAttribute("data-skip-link") || anchor.download || anchor.target && anchor.target !== "_self") return;
  const destination = new URL(anchor.href, window.location.href);
  if (destination.origin !== location.origin || destination.pathname !== location.pathname || destination.search !== location.search || destination.hash === location.hash) return;
  // Error-summary and in-page anchors name existing elements. Their local focus
  // handler runs after this capture listener; they are not application routes.
  if (document.getElementById(destination.hash.slice(1))) return;
  event.preventDefault();
  confirmNavigation(() => visit(destination.hash));
}, true);

export function useRoute(): Route {
  return useSyncExternalStore((notify) => { listeners.add(notify); return () => { listeners.delete(notify); }; }, () => current);
}

export function go(to: string): void {
  const destination = href(to);
  if (destination === window.location.hash) return;
  confirmNavigation(() => visit(destination));
}

export function href(to: string): string {
  return to.startsWith("#") ? to : `#${to}`;
}

/** Guard a full-document action (for example ending the session) before any side effect. */
export function confirmNavigation(proceed: () => void): void {
  if (!blocked(proceed)) proceed();
}

/** Replace route metadata after dismissing its action, retaining the current history entry. */
export function replaceHash(to: string): void {
  window.history.replaceState({ ...window.history.state, taRouteIndex: currentIndex }, "", href(to));
  commit(currentIndex);
}
